import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getUserPermissions } from '../services/api/permissionsApi';
import {
  amplifySignIn,
  amplifySignUp,
  amplifySignOut,
  amplifyConfirmSignUp,
  amplifyConfirmSignIn,
  amplifyGetCurrentUser,
  amplifyResetPassword,
  amplifyConfirmResetPassword,
  isLocalStack,
} from '../services/amplifyConfig';

// Mock user for LocalStack development
const MOCK_USER = {
  username: 'test@localstack.com',
  attributes: {
    email: 'test@localstack.com',
    given_name: 'Test',
    family_name: 'User'
  }
};

// Mock permissions for LocalStack
const MOCK_PERMISSIONS = [
  { resource: 'extractions', action: 'view' },
  { resource: 'extractions', action: 'create' },
  { resource: 'extractions', action: 'manage' },
  { resource: 'fields', action: 'view' },
  { resource: 'fields', action: 'create' },
  { resource: 'fields', action: 'edit' },
  { resource: 'fields', action: 'delete' },
  { resource: 'invoices', action: 'analyze' },
  { resource: 'permissions', action: 'manage' },
  { resource: 'customers', action: 'view' },
  { resource: 'customers', action: 'manage' },
  { resource: 'rulesets', action: 'view' },
  { resource: 'rulesets', action: 'create' },
  { resource: 'rulesets', action: 'edit' },
  { resource: 'rulesets', action: 'delete' },
  { resource: 'metadata', action: 'view' },
  { resource: 'metadata', action: 'create' },
  { resource: 'metadata', action: 'edit' },
  { resource: 'metadata', action: 'delete' },
  { resource: 'customers', action: 'list' },
  { resource: 'extraction_results', action: 'view' },
  { resource: 'extraction_metadata', action: 'view' },
  { resource: 'extraction_comparison', action: 'view' },
  { resource: 'extraction_discrepancies', action: 'view' },
  { resource: 'extraction_logs', action: 'view' },
  { resource: 'extraction_metrics', action: 'view' },
  { resource: 'extraction_playground', action: 'view' },
  { resource: 'extraction_prompts', action: 'view' },
  { resource: 'augmentation', action: 'manage' },
  { resource: 'augmentation', action: 'review' },
  { resource: 'reports', action: 'view' },
  { resource: 'model_config', action: 'view' },
  { resource: 'model_config', action: 'manage' },
  { resource: 'approved_models', action: 'view' },
  { resource: 'approved_models', action: 'manage' }
];

const MOCK_AUTH_STORAGE_KEY = 'iris-mock-auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function checkUser() {
      if (isLocalStack) {
        // Restore mock auth from localStorage if available (survives page refresh)
        try {
          const stored = localStorage.getItem(MOCK_AUTH_STORAGE_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            setUser(parsed.user);
            setPermissions(parsed.permissions);
          }
        } catch {
          // Corrupted storage — ignore and require fresh login
        }
        setLoading(false);
        return;
      }
      
      try {
        const currentUser = await amplifyGetCurrentUser();
        setUser(currentUser);
        await fetchUserPermissions();
      } catch (error) {
        setUser(null);
        setPermissions([]);
      }
      setLoading(false);
    }
    
    checkUser();
  }, [isLocalStack]);

  const fetchUserPermissions = async () => {
    try {
      const response = await getUserPermissions();
      setPermissions(response.data);
    } catch (error) {
      console.warn('Failed to fetch user permissions:', error);
      setPermissions([]);
    }
  };

  const signIn = async (username, password) => {
    if (isLocalStack) {
      // Mock sign in for LocalStack - accept any credentials for test@localstack.com
      if (username === 'test@localstack.com') {
        setUser(MOCK_USER);
        setPermissions(MOCK_PERMISSIONS);
        // Persist to localStorage so auth survives page refresh (enables Playwright storageState)
        localStorage.setItem(MOCK_AUTH_STORAGE_KEY, JSON.stringify({ user: MOCK_USER, permissions: MOCK_PERMISSIONS }));
        return MOCK_USER;
      } else {
        throw new Error('Invalid username or password');
      }
    }
    
    try {
      // Clear any stale session before signing in
      try {
        await amplifySignOut();
      } catch (_) {
        // Ignore — no session to clear
      }
      const result = await amplifySignIn({ username, password });
      // In v6, signIn returns { isSignedIn, nextStep } — fetch user separately
      if (result.isSignedIn) {
        const currentUser = await amplifyGetCurrentUser();
        setUser(currentUser);
        if (permissions.length === 0) {
          await fetchUserPermissions();
        }
        return currentUser;
      }
      // Return the result for flows that need additional steps (e.g., NEW_PASSWORD_REQUIRED)
      return result;
    } catch (error) {
      throw error;
    }
  };

  const completeNewPassword = async (challengeResponse) => {
    try {
      // In v6, use confirmSignIn to complete the NEW_PASSWORD_REQUIRED challenge
      const result = await amplifyConfirmSignIn({ challengeResponse });
      if (result.isSignedIn) {
        const currentUser = await amplifyGetCurrentUser();
        setUser(currentUser);
        if (permissions.length === 0) {
          await fetchUserPermissions();
        }
        return currentUser;
      }
      return result;
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (username, password, email) => {
    try {
      const result = await amplifySignUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      return result;
    } catch (error) {
      throw error;
    }
  };

  const confirmSignUp = async (username, confirmationCode) => {
    try {
      await amplifyConfirmSignUp({ username, confirmationCode });
    } catch (error) {
      throw error;
    }
  };

  const signOut = async () => {
    if (isLocalStack) {
      // Mock sign out for LocalStack
      setUser(null);
      setPermissions([]);
      localStorage.removeItem(MOCK_AUTH_STORAGE_KEY);
      return;
    }
    
    try {
      await amplifySignOut();
      setUser(null);
      setPermissions([]);
    } catch (error) {
      throw error;
    }
  };

  const hasPermission = (resource, action) => {
    return Array.isArray(permissions) && permissions.some(p => p.resource === resource && p.action === action);
  };

  const refreshUser = async () => {
    if (isLocalStack) {
      return;
    }
    
    try {
      const currentUser = await amplifyGetCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  const forgotPassword = async (username) => {
    try {
      await amplifyResetPassword({ username });
    } catch (error) {
      throw error;
    }
  };

  const forgotPasswordSubmit = async (username, confirmationCode, newPassword) => {
    try {
      await amplifyConfirmResetPassword({ username, confirmationCode, newPassword });
    } catch (error) {
      throw error;
    }
  };

  const value = {
    user,
    permissions,
    loading,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    forgotPassword,
    forgotPasswordSubmit,
    completeNewPassword,
    hasPermission,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
